/**
 * Comportamentos das seções no modelo Lumina (home). Cada parte só age se o elemento dela
 * existir na página, e tudo é reiniciado quando o editor de temas recarrega uma seção.
 *
 * - [data-bz-marquee]: faixas rolando em loop; duplica o grupo até cobrir 2x a tela e ajusta a
 *   duração pela largura (data-speed = pixels por segundo).
 * - .bz-rv: entrada suave ao aparecer na tela.
 * - [data-bz-header]: cabeçalho que encolhe ao rolar, gaveta do menu, painel de busca.
 * - [data-bz-hero]: carrossel com barras de progresso, contador, setas e arrastar.
 * - [data-bz-rail]: trilhos horizontais com setas (e barra de progresso na vitrine).
 * - [data-bz-tab] / [data-bz-need]: filtros da vitrine "Mais vendidos".
 * - [data-bz-bento]: cards de "Fórmula e tecnologia" abrem ao tocar.
 * - [data-bz-buy] / [data-bz-add]: adicionam à sacola sem sair da página.
 * - [data-bz-parallax]: faixa de vídeo com parallax.
 * - [data-bz-footer]: rodapé que surge por trás da página + acordeão + newsletter.
 * - [data-bz-sticky]: barra fixa do celular que leva ao quiz.
 */
(() => {
  if (window.lumina) return;

  const $ = (selector, scope = document) => scope.querySelector(selector);
  const $$ = (selector, scope = document) => Array.from(scope.querySelectorAll(selector));
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const design = Boolean(window.Shopify && window.Shopify.designMode);
  const smooth = reduce ? 'auto' : 'smooth';

  /* ---------- Medição: quiz, "Compre junto" e "Complete sua rotina" ----------
   * Eventos com nomes próprios (não substituem AddToCart/Purchase, então não mexem na otimização
   * das campanhas nem nas UTMs). Vão para os pixels personalizados da Shopify (Eventos de clientes),
   * GA4 (gtag/dataLayer) e Pixel da Meta (trackCustom), quando estiverem na página. */
  const track = (name, data = {}) => {
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') window.Shopify.analytics.publish(name, data);
      if (typeof window.gtag === 'function') window.gtag('event', name, data);
      else if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: name, ...data });
      if (typeof window.fbq === 'function') window.fbq('trackCustom', name, data);
    } catch (error) {
      // medição nunca pode atrapalhar a compra
    }
  };
  window.bzTrack = track;

  /* ---------- Aviso "adicionado à sacola" ---------- */
  let toastEl = null;
  const toast = (message) => {
    if (!toastEl) {
      const config = document.querySelector('[data-bz-toast-config]');
      toastEl = document.createElement('div');
      toastEl.className = 'bz bz-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      toastEl.innerHTML = `<span></span><button type="button">${(config && config.dataset.view) || 'Ver sacola'}</button>`;
      toastEl.querySelector('button').addEventListener('click', () => {
        toastEl.classList.remove('on');
        if (window.themeCart) window.themeCart.open();
        else window.location.href = `${root}cart`;
      });
      document.body.append(toastEl);
    }
    toastEl.querySelector('span').textContent = message;
    toastEl.classList.add('on');
    clearTimeout(toastEl.timer);
    toastEl.timer = setTimeout(() => toastEl.classList.remove('on'), 2800);
  };

  // origem: marca invisível (_origem) no item, que segue para o pedido (ex.: 'complete-rotina')
  const addToCart = async (variantId, quantity = 1, origem = '') => {
    const item = { id: Number(variantId), quantity };
    if (origem) item.properties = { _origem: origem };
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [item] }),
    });
    if (!response.ok) throw new Error(String(response.status));
    if (window.themeCart) await window.themeCart.refresh();
  };

  const toastText = (name) => {
    const config = document.querySelector('[data-bz-toast-config]');
    const template = (config && config.dataset.added) || '[produto] na sacola';
    return template.replace('[produto]', name || '');
  };

  /* ---------- Prévia rápida do produto (produto com variantes) ----------
   * Botões [data-bz-quick="handle"] (cards da vitrine, vídeos de influencers, sugestões da sacola)
   * abrem um pop-up com uma mini página do produto: galeria, preço, parcelamento, resumo da
   * descrição, opções (kits), quantidade e o botão de adicionar. Nada entra na sacola até a pessoa
   * confirmar. Dados de /products/handle.js (guardados em memória). Se algo falhar, abre a página.
   */
  const quickCache = new Map();
  let quickEl = null;
  let quick = null;
  const quickConfig = () => {
    const el = document.querySelector('[data-bz-toast-config]');
    return (el && el.dataset) || {};
  };
  const centsToMoney = (cents) =>
    new Intl.NumberFormat(document.documentElement.lang || 'pt-BR', { style: 'currency', currency: quickConfig().currency || 'BRL' }).format(
      (Number(cents) || 0) / 100
    );
  const imageUrl = (src) => (typeof src === 'string' ? src : (src && src.src) || '');
  const sizedImage = (src, width) => {
    const url = imageUrl(src);
    return url ? `${url}${url.includes('?') ? '&' : '?'}width=${width}` : '';
  };
  const imageKey = (src) => imageUrl(src).split('?')[0].replace(/^https?:/, '');
  const excerpt = (html, max = 190) => {
    const text = (new DOMParser().parseFromString(html || '', 'text/html').body.textContent || '').replace(/\s+/g, ' ').trim();
    return text.length > max ? `${text.slice(0, max).replace(/\s+\S*$/, '')}…` : text;
  };
  const icon = (d) => `<svg class="bz-i" viewBox="0 0 24 24" aria-hidden="true"><path d="${d}"/></svg>`;
  const priceLine = (cents) => {
    const c = quickConfig();
    const n = Number(c.installments) || 4;
    const pix = Number(c.pix) || 0;
    let line = (c.priceLine || '[n]x de [parcela]')
      .replace('[n]', String(n))
      .replace('[parcela]', centsToMoney(Math.round(cents / n)))
      .replace('[pix]', centsToMoney(Math.round((cents * (100 - pix)) / 100)));
    if (!pix) line = line.split(' ou ')[0];
    return line;
  };

  const quickVariant = () => quick && quick.product.variants.find((v) => v.options.every((value, i) => value === quick.selected[i]));
  // Opção disponível se existir variante à venda com ela + as demais escolhas atuais
  const quickAvailable = (index, value) =>
    quick.product.variants.some((v) => v.available && v.options[index] === value && v.options.every((opt, i) => i === index || opt === quick.selected[i]));

  /* Galeria: montada uma vez por produto; trocar a opção só rola até a foto da variante */
  const renderGallery = () => {
    const gallery = $('[data-bz-quick-gallery]', quickEl);
    const { product } = quick;
    const images = (product.images && product.images.length ? product.images : [product.featured_image]).filter(Boolean);
    quick.imageKeys = images.map(imageKey);
    if (!images.length) {
      gallery.innerHTML = '<div class="bz-qv__track"><div class="bz-qv__slide"></div></div>';
      return;
    }
    const slides = images
      .map(
        (src, i) =>
          `<figure class="bz-qv__slide"><img src="${escapeHtml(sizedImage(src, 900))}" srcset="${escapeHtml(sizedImage(src, 500))} 500w, ${escapeHtml(sizedImage(src, 900))} 900w, ${escapeHtml(sizedImage(src, 1300))} 1300w" sizes="(min-width: 768px) 480px, 100vw" alt="${escapeHtml(i === 0 ? product.title : '')}"${i === 0 ? '' : ' loading="lazy"'}></figure>`
      )
      .join('');
    const many = images.length > 1;
    const arrows = many
      ? `<button type="button" class="bz-qv__arrow bz-qv__arrow--prev" data-bz-qv-step="-1" aria-label="Foto anterior">${icon('m15 6-6 6 6 6')}</button><button type="button" class="bz-qv__arrow bz-qv__arrow--next" data-bz-qv-step="1" aria-label="Próxima foto">${icon('m9 6 6 6-6 6')}</button>`
      : '';
    const dots = many
      ? `<div class="bz-qv__dots">${images.map((_, i) => `<button type="button" data-bz-qv-dot="${i}" aria-label="Foto ${i + 1}"${i === 0 ? ' aria-current="true"' : ''}></button>`).join('')}</div>`
      : '';
    gallery.innerHTML = `<div class="bz-qv__track bz-noscroll" data-bz-qv-track>${slides}</div>${arrows}${dots}`;
    const track = $('[data-bz-qv-track]', gallery);
    if (track && many) {
      track.addEventListener(
        'scroll',
        () => {
          const index = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
          $$('[data-bz-qv-dot]', gallery).forEach((dot, i) => (i === index ? dot.setAttribute('aria-current', 'true') : dot.removeAttribute('aria-current')));
        },
        { passive: true }
      );
    }
  };
  const galleryIndex = () => {
    const track = quickEl && $('[data-bz-qv-track]', quickEl);
    return track ? Math.round(track.scrollLeft / Math.max(1, track.clientWidth)) : 0;
  };
  const galleryGo = (index, behavior = smooth) => {
    const track = quickEl && $('[data-bz-qv-track]', quickEl);
    if (!track) return;
    const next = Math.max(0, Math.min(track.children.length - 1, index));
    track.scrollTo({ left: next * track.clientWidth, behavior });
  };
  // Foto da variante escolhida (quando o produto tem foto por variante)
  const showVariantImage = (behavior) => {
    const variant = quickVariant();
    if (!variant || !variant.featured_image || !quick.imageKeys) return;
    const index = quick.imageKeys.indexOf(imageKey(variant.featured_image));
    if (index >= 0) galleryGo(index, behavior);
  };

  const renderQuick = (focusKey) => {
    const body = $('[data-bz-quick-body]', quickEl);
    const addBtn = $('[data-bz-quick-add]', quickEl);
    const link = $('[data-bz-quick-link]', quickEl);
    const c = quickConfig();
    const { product } = quick;
    const variant = quickVariant();
    const price = variant ? variant.price : product.price;
    const compare = variant ? variant.compare_at_price : product.compare_at_price;
    const off = compare > price ? Math.round(((compare - price) * 100) / compare) : 0;
    const single = product.options.length === 1;
    const summary = excerpt(product.description);

    const options = product.options
      .map((option, index) => {
        const name = typeof option === 'string' ? option : option.name;
        const values = typeof option === 'string' ? [...new Set(product.variants.map((v) => v.options[index]))] : option.values;
        if (values.length === 1 && values[0] === 'Default Title') return '';
        const buttons = values
          .map((value) => {
            const on = value === quick.selected[index];
            const available = quickAvailable(index, value);
            let extra = '';
            if (single) {
              const v = product.variants.find((item) => item.options[0] === value);
              if (v) {
                const off = v.compare_at_price > v.price ? Math.round(((v.compare_at_price - v.price) * 100) / v.compare_at_price) : 0;
                extra = `<span class="bz-qa__vprice">${off ? `<em>-${off}%</em>` : ''}<b>${centsToMoney(v.price)}</b></span>`;
              }
            }
            return `<button type="button" class="bz-qa__val${on ? ' on' : ''}${available ? '' : ' off'}" aria-pressed="${on}" data-bz-quick-opt="${index}" data-value="${escapeHtml(value)}"><span class="bz-qa__vname">${escapeHtml(value)}</span>${extra}</button>`;
          })
          .join('');
        return `<fieldset class="bz-qa__opt${single ? ' bz-qa__opt--list' : ''}"><legend>${escapeHtml(name)}: <b>${escapeHtml(quick.selected[index])}</b></legend><div class="bz-qa__vals">${buttons}</div></fieldset>`;
      })
      .join('');

    body.innerHTML = `
      <div class="bz-qv__intro">
        <h3 id="BzQuickTitle" class="bz-qv__title">${escapeHtml(product.title)}</h3>
        <div class="bz-qa__pr">${compare > price ? `<s>${centsToMoney(compare)}</s>` : ''}<span class="now">${centsToMoney(price)}</span>${off ? `<span class="bz-bdg off">-${off}%</span>` : ''}</div>
        <span class="bz-qa__inst">${escapeHtml(priceLine(price))}</span>
        ${summary ? `<p class="bz-qv__desc">${escapeHtml(summary)}</p>` : ''}
      </div>
      ${options}
      <div class="bz-qa__qty">
        <span>${escapeHtml(c.quickQuantity || 'Quantidade')}</span>
        <div class="bz-qty">
          <button type="button" data-bz-quick-qty="-1" aria-label="−"${quick.qty <= 1 ? ' disabled' : ''}>${icon('M5 12h14')}</button>
          <span aria-live="polite">${quick.qty}</span>
          <button type="button" data-bz-quick-qty="1" aria-label="+">${icon('M12 5v14M5 12h14')}</button>
        </div>
      </div>`;

    const available = Boolean(variant && variant.available);
    addBtn.disabled = !available;
    addBtn.textContent = available ? `${c.quickButton || 'Adicionar à sacola'} · ${centsToMoney(price * quick.qty)}` : c.quickSoldOut || 'Esgotado';
    link.href = variant ? `${quick.url}${quick.url.includes('?') ? '&' : '?'}variant=${variant.id}` : quick.url;
    if (focusKey) {
      const again = $(`[data-bz-quick-opt="${focusKey.opt}"][data-value="${CSS.escape(focusKey.value)}"], [data-bz-quick-qty="${focusKey.qty}"]`, body);
      if (again) again.focus();
    }
  };

  const ensureQuick = () => {
    if (quickEl) return quickEl;
    const c = quickConfig();
    quickEl = document.createElement('dialog');
    quickEl.className = 'bz bz-qa bz-qv color-scheme-1';
    quickEl.setAttribute('scroll-lock', '');
    quickEl.setAttribute('aria-labelledby', 'BzQuickTitle');
    quickEl.innerHTML = `
      <button type="button" class="bz-icon-btn bz-qv__close" data-bz-quick-close aria-label="${escapeHtml(c.quickClose || 'Fechar')}">${icon('M6 6l12 12M18 6 6 18')}</button>
      <div class="bz-qv__grid">
        <div class="bz-qv__gallery" data-bz-quick-gallery></div>
        <div class="bz-qv__side">
          <div class="bz-qa__body" data-bz-quick-body></div>
          <div class="bz-qa__foot">
            <button type="button" class="bz-btn" data-bz-quick-add disabled></button>
            <a class="bz-qa__more" data-bz-quick-link href="#">${escapeHtml(c.quickDetails || 'Ver página completa do produto')}</a>
          </div>
        </div>
      </div>`;
    document.body.append(quickEl);

    quickEl.addEventListener('click', async (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      // Fundo escurecido (fora do painel) ou botão fechar
      if (target === quickEl) {
        const r = quickEl.getBoundingClientRect();
        if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) quickEl.close();
        return;
      }
      if (target.closest('[data-bz-quick-close]')) {
        quickEl.close();
        return;
      }
      const step = target.closest('[data-bz-qv-step]');
      if (step) {
        galleryGo(galleryIndex() + Number(step.dataset.bzQvStep));
        return;
      }
      const dot = target.closest('[data-bz-qv-dot]');
      if (dot) {
        galleryGo(Number(dot.dataset.bzQvDot));
        return;
      }
      if (!quick) return;
      const opt = target.closest('[data-bz-quick-opt]');
      if (opt) {
        const index = Number(opt.dataset.bzQuickOpt);
        quick.selected[index] = opt.dataset.value;
        // Combinação inexistente/esgotada: pula para a 1ª variante à venda com o valor escolhido
        const current = quickVariant();
        if (!current || !current.available) {
          const fallback = quick.product.variants.find((v) => v.available && v.options[index] === opt.dataset.value);
          if (fallback) quick.selected = [...fallback.options];
        }
        renderQuick({ opt: index, value: opt.dataset.value });
        showVariantImage();
        return;
      }
      const qty = target.closest('[data-bz-quick-qty]');
      if (qty) {
        quick.qty = Math.max(1, Math.min(99, quick.qty + Number(qty.dataset.bzQuickQty)));
        renderQuick({ qty: qty.dataset.bzQuickQty });
        return;
      }
      const add = target.closest('[data-bz-quick-add]');
      if (add) {
        const variant = quickVariant();
        if (!variant || !variant.available) return;
        add.disabled = true;
        add.setAttribute('aria-busy', 'true');
        try {
          const origem = quick.opener.dataset.bzOrigem || '';
          await addToCart(variant.id, quick.qty, origem);
          if (origem === 'complete-rotina') track('rotina_adicionou', { produto: quick.product.title, variante: variant.id, valor: (variant.price * quick.qty) / 100 });
          quickEl.close();
          if (window.themeCart && document.querySelector('[data-cart-drawer]')) window.themeCart.open();
          else toast(toastText(quick.product.title));
        } catch (error) {
          const origem = quick.opener.dataset.bzOrigem;
          window.location.href = `${root}cart/add?id=${variant.id}&quantity=${quick.qty}${origem ? `&properties[_origem]=${encodeURIComponent(origem)}` : ''}`;
        } finally {
          add.disabled = false;
          add.removeAttribute('aria-busy');
        }
      }
    });
    quickEl.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (!(event.target instanceof Element) || !event.target.closest('[data-bz-quick-gallery]')) return;
      galleryGo(galleryIndex() + (event.key === 'ArrowRight' ? 1 : -1));
    });
    quickEl.addEventListener('close', () => {
      if (quick && quick.opener && document.contains(quick.opener)) quick.opener.focus({ preventScroll: true });
    });
    return quickEl;
  };

  const openQuick = async (opener) => {
    const handle = opener.dataset.bzQuick;
    const url = opener.dataset.bzQuickUrl || `${root}products/${handle}`;
    ensureQuick();
    quick = null;
    $('[data-bz-quick-gallery]', quickEl).innerHTML = '<div class="bz-qa__loading bz-qv__loading"></div>';
    $('[data-bz-quick-body]', quickEl).innerHTML = '<div class="bz-qa__loading" aria-busy="true"></div>';
    $('[data-bz-quick-add]', quickEl).disabled = true;
    $('[data-bz-quick-link]', quickEl).href = url;
    if (!quickEl.open) quickEl.showModal();
    try {
      if (!quickCache.has(handle)) {
        const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(String(response.status));
        quickCache.set(handle, await response.json());
      }
      const product = quickCache.get(handle);
      const first = product.variants.find((v) => v.available) || product.variants[0];
      quick = { product, url, opener, qty: 1, selected: [...first.options] };
      renderGallery();
      renderQuick();
      showVariantImage('auto');
    } catch (error) {
      window.location.href = url;
    }
  };

  /* ---------- Faixas em loop ---------- */
  const initMarquee = (el) => {
    const track = $('.bz-mq-track', el);
    if (!track || !track.firstElementChild) return;
    const group = track.firstElementChild;
    $$('[data-bz-clone]', track).forEach((clone) => clone.remove());
    const width = Math.max(1, group.offsetWidth);
    const need = Math.ceil((window.innerWidth * 2) / width) + 1;
    for (let i = 1; i < need; i += 1) {
      const clone = group.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.dataset.bzClone = '';
      clone.removeAttribute('data-shopify-editor-block');
      $$('[data-shopify-editor-block]', clone).forEach((node) => node.removeAttribute('data-shopify-editor-block'));
      track.append(clone);
    }
    if (track.children.length % 2) {
      const clone = group.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.dataset.bzClone = '';
      track.append(clone);
    }
    const speed = Number(el.dataset.speed) || 45;
    track.style.animationDuration = `${(width * track.children.length) / 2 / speed}s`;
    if (marqueeObserver && !el.dataset.bzMqWatch) {
      el.dataset.bzMqWatch = 'true';
      marqueeObserver.observe(el);
    }
  };
  // Pausa as faixas que saíram da tela (várias na home: especialistas, selos, mídia...)
  const marqueeObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver((entries) => entries.forEach((entry) => entry.target.classList.toggle('bz-mq-off', !entry.isIntersecting)), {
          rootMargin: '100px 0px',
        })
      : null;

  /* ---------- Entrada ao rolar ---------- */
  // Primeira resposta do observador = o que já está na tela ao abrir: aparece sem fade
  let revealFirst = true;
  const revealObserver =
    'IntersectionObserver' in window && !reduce && !design
      ? new IntersectionObserver(
          (entries) => {
            const instant = revealFirst;
            revealFirst = false;
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              if (instant) entry.target.classList.add('bz-rv-now');
              entry.target.classList.add('in');
              revealObserver.unobserve(entry.target);
            });
          },
          { threshold: 0, rootMargin: '0px 0px -4% 0px' }
        )
      : null;
  const initReveal = (scope) => {
    $$('.bz-rv', scope).forEach((el) => (revealObserver ? revealObserver.observe(el) : el.classList.add('in')));
  };

  /* ---------- Painéis (gaveta do menu e busca) ---------- */
  let lockY = null;
  let lastFocus = null;
  const lock = () => {
    if (lockY !== null) return;
    lockY = window.scrollY;
    Object.assign(document.body.style, { position: 'fixed', top: `${-lockY}px`, left: '0', right: '0' });
  };
  const unlock = () => {
    if (lockY === null) return;
    Object.assign(document.body.style, { position: '', top: '', left: '', right: '' });
    document.documentElement.style.scrollBehavior = 'auto';
    window.scrollTo(0, lockY);
    document.documentElement.style.scrollBehavior = '';
    lockY = null;
  };
  const scrim = () => $('[data-bz-scrim]');
  const closePanels = () => {
    const open = $$('[data-bz-panel].on, [data-bz-panel].open');
    open.forEach((panel) => {
      panel.classList.remove('on', 'open');
      panel.setAttribute('aria-hidden', 'true');
    });
    const s = scrim();
    if (s) {
      s.classList.remove('on');
      setTimeout(() => {
        if (!s.classList.contains('on')) s.hidden = true;
      }, 300);
    }
    document.body.classList.remove('bz-lock');
    unlock();
    if (open.length && lastFocus) lastFocus.focus({ preventScroll: true });
  };
  const openPanel = (id) => {
    const panel = document.getElementById(id);
    if (!panel) return;
    const wasOpen = $$('[data-bz-panel].on, [data-bz-panel].open').length > 0;
    if (wasOpen) {
      $$('[data-bz-panel].on, [data-bz-panel].open').forEach((p) => p.classList.remove('on', 'open'));
    } else {
      lastFocus = document.activeElement;
    }
    const isSearch = panel.hasAttribute('data-bz-search');
    if (!isSearch) {
      const s = scrim();
      if (s) {
        s.hidden = false;
        requestAnimationFrame(() => s.classList.add('on'));
      }
    } else if (scrim()) {
      scrim().classList.remove('on');
      scrim().hidden = true;
    }
    panel.classList.add(isSearch ? 'open' : 'on');
    panel.setAttribute('aria-hidden', 'false');
    document.body.classList.add('bz-lock');
    lock();
    setTimeout(() => {
      const focus = isSearch ? $('[data-bz-search-input]', panel) : $('button, a', panel);
      if (focus) focus.focus({ preventScroll: true });
    }, 80);
    if (isSearch) runSearch(panel);
  };

  /* ---------- Busca ao vivo ---------- */
  const money = (value) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'pt-BR', { style: 'currency', currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'BRL' }).format(Number(value));
    } catch (error) {
      return value;
    }
  };
  const escapeHtml = (text) => String(text).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  let searchTimer = null;
  let searchAbort = null;
  const runSearch = (panel) => {
    const input = $('[data-bz-search-input]', panel);
    const list = $('[data-bz-search-list]', panel);
    const label = $('[data-bz-search-label]', panel);
    const template = $('template[data-bz-search-default]', panel);
    if (!input || !list || !label) return;
    const term = input.value.trim();
    clearTimeout(searchTimer);
    if (!term) {
      label.textContent = label.dataset.default;
      if (template) list.innerHTML = template.innerHTML;
      return;
    }
    searchTimer = setTimeout(async () => {
      if (searchAbort) searchAbort.abort();
      searchAbort = new AbortController();
      try {
        const url = `${root}search/suggest.json?q=${encodeURIComponent(term)}&resources[type]=product&resources[limit]=8`;
        const response = await fetch(url, { signal: searchAbort.signal });
        const data = await response.json();
        const products = (data.resources && data.resources.results && data.resources.results.products) || [];
        if (!products.length) {
          label.textContent = label.dataset.none;
          list.innerHTML = `<p class="bz-small">${escapeHtml(label.dataset.empty.replace('[termo]', term))}</p>`;
          return;
        }
        label.textContent = (products.length === 1 ? label.dataset.one : label.dataset.many).replace('[n]', products.length).replace('[termo]', term);
        list.innerHTML = products
          .map(
            (p) => `<a href="${escapeHtml(p.url)}" class="bz-sitem"><div class="th">${p.image ? `<img src="${escapeHtml(p.image)}&width=160" alt="" loading="lazy">` : ''}</div>
              <div><b>${escapeHtml(p.title)}</b><span class="bz-small">${Number(p.price) > 0 ? money(p.price) : ''}</span></div></a>`
          )
          .join('');
      } catch (error) {
        /* busca cancelada ou sem rede: mantém o que está na tela */
      }
    }, 250);
  };

  const initHeader = (header) => {
    if (header.dataset.bzReady) return;
    header.dataset.bzReady = 'true';
    let ticking = false;
    let lastHeight = -1;
    const update = () => {
      ticking = false;
      // Mede antes de mexer na classe e só reescreve a variável do <html> quando ela muda
      const height = Math.round(header.getBoundingClientRect().bottom);
      header.classList.toggle('scrolled', window.scrollY > 30);
      if (height === lastHeight) return;
      lastHeight = height;
      document.documentElement.style.setProperty('--header-height', `${height}px`);
    };
    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      },
      { passive: true }
    );
    update();
  };

  /* ---------- Carrossel do topo ---------- */
  const initHero = (hero) => {
    if (hero.dataset.bzReady) return;
    hero.dataset.bzReady = 'true';
    const slides = $$('.bz-slide', hero);
    const dots = $$('.bz-dot', hero);
    const count = $('[data-bz-count]', hero);
    const duration = Number(hero.dataset.duration) || 0;
    let index = 0;
    let timer = null;
    const pad = (n) => String(n).padStart(2, '0');

    const schedule = () => {
      clearTimeout(timer);
      if (!duration || reduce || design || hero.classList.contains('paused') || slides.length < 2) return;
      timer = setTimeout(() => go(index + 1), duration);
    };
    const go = (n) => {
      index = (n + slides.length) % slides.length;
      slides.forEach((slide, i) => {
        slide.classList.toggle('on', i === index);
        slide.setAttribute('aria-hidden', String(i !== index));
      });
      dots.forEach((dot, i) => {
        dot.classList.remove('on', 'done');
        void dot.offsetWidth;
        if (i < index) dot.classList.add('done');
        if (i === index) dot.classList.add('on');
        dot.setAttribute('aria-selected', String(i === index));
      });
      if (count) count.textContent = `${pad(index + 1)}/${pad(slides.length)}`;
      schedule();
    };
    hero.bzGo = go;
    dots.forEach((dot, i) => dot.addEventListener('click', () => go(i)));
    const prev = $('[data-bz-prev]', hero);
    const next = $('[data-bz-next]', hero);
    if (prev) prev.addEventListener('click', () => go(index - 1));
    if (next) next.addEventListener('click', () => go(index + 1));
    hero.addEventListener('mouseenter', () => {
      hero.classList.add('paused');
      clearTimeout(timer);
    });
    hero.addEventListener('mouseleave', () => {
      hero.classList.remove('paused');
      go(index);
    });
    hero.addEventListener('focusin', () => {
      hero.classList.add('paused');
      clearTimeout(timer);
    });
    let tx = 0;
    let ty = 0;
    hero.addEventListener(
      'touchstart',
      (event) => {
        tx = event.touches[0].clientX;
        ty = event.touches[0].clientY;
      },
      { passive: true }
    );
    hero.addEventListener(
      'touchend',
      (event) => {
        const dx = event.changedTouches[0].clientX - tx;
        const dy = event.changedTouches[0].clientY - ty;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) go(index + (dx < 0 ? 1 : -1));
      },
      { passive: true }
    );
    document.addEventListener('visibilitychange', () => (document.hidden ? clearTimeout(timer) : schedule()));
    schedule();
  };

  /* ---------- Trilhos com setas ---------- */
  const initRail = (section) => {
    const rail = $('[data-bz-rail]', section);
    if (!rail || rail.dataset.bzReady) return;
    rail.dataset.bzReady = 'true';
    const bar = $('[data-bz-rail-bar]', section);
    const prev = $('[data-bz-rail-prev]', section);
    const next = $('[data-bz-rail-next]', section);
    const visible = () => $$(':scope > *:not([hidden])', rail);
    const step = () => {
      const first = visible()[0];
      const gap = parseFloat(getComputedStyle(rail).columnGap) || 20;
      return first ? first.getBoundingClientRect().width + gap : 300;
    };
    const update = () => {
      const max = rail.scrollWidth - rail.clientWidth;
      if (bar) {
        const w = Math.min(1, rail.clientWidth / Math.max(1, rail.scrollWidth));
        bar.style.width = `${w * 100}%`;
        bar.style.transform = `translateX(${max > 0 ? (rail.scrollLeft / max) * (1 / w - 1) * 100 : 0}%)`;
      }
      if (prev) prev.disabled = rail.scrollLeft < 4;
      if (next) next.disabled = rail.scrollLeft > max - 4;
    };
    if (prev) prev.addEventListener('click', () => rail.scrollBy({ left: -step(), behavior: smooth }));
    if (next) next.addEventListener('click', () => rail.scrollBy({ left: step(), behavior: smooth }));
    rail.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    rail.bzUpdate = update;
    update();
    if (section.matches('[data-bz-rail-section]')) initTabs(section, rail);
  };

  /* ---------- Abas e filtro por necessidade da vitrine ---------- */
  const initTabs = (section, rail) => {
    const tabs = $$('[data-bz-tab]', section);
    const chip = $('[data-bz-need-filter]', section);
    const filter = (handle) => {
      rail.classList.add('fading');
      setTimeout(
        () => {
          $$(':scope > .bz-card', rail).forEach((card) => {
            const cols = ` ${card.dataset.cols || ''} `;
            card.hidden = !(handle === 'all' || cols.includes(` ${handle} `));
          });
          rail.scrollLeft = 0;
          rail.classList.remove('fading');
          if (rail.bzUpdate) rail.bzUpdate();
        },
        reduce ? 0 : 180
      );
    };
    const showTab = (handle) => {
      if (chip) chip.classList.remove('on');
      tabs.forEach((tab) => tab.setAttribute('aria-selected', String(tab.dataset.bzTab === handle)));
      filter(handle);
    };
    tabs.forEach((tab) => tab.addEventListener('click', () => showTab(tab.dataset.bzTab)));
    if (chip) {
      $('button', chip).addEventListener('click', () => showTab(tabs[0] ? tabs[0].dataset.bzTab : 'all'));
    }
    section.bzShowNeed = (handle, label) => {
      tabs.forEach((tab) => tab.setAttribute('aria-selected', 'false'));
      if (chip) {
        chip.classList.add('on');
        $('span', chip).textContent = `${section.dataset.needPrefix || ''} ${label}`.trim();
      }
      filter(handle);
    };
    const initial = tabs.find((tab) => tab.getAttribute('aria-selected') === 'true');
    if (initial && initial.dataset.bzTab !== 'all') filter(initial.dataset.bzTab);
  };

  /* ---------- Bento "Fórmula e tecnologia" ---------- */
  const initBento = (grid) => {
    if (grid.dataset.bzReady) return;
    grid.dataset.bzReady = 'true';
    grid.addEventListener('click', (event) => {
      if (event.target.closest('a')) return;
      const card = event.target.closest('.bz-ax');
      if (!card) return;
      const open = !card.classList.contains('open');
      $$('.bz-ax.open', grid).forEach((other) => {
        if (other !== card) {
          other.classList.remove('open');
          other.setAttribute('aria-expanded', 'false');
        }
      });
      card.classList.toggle('open', open);
      card.setAttribute('aria-expanded', String(open));
    });
    grid.addEventListener('keydown', (event) => {
      if ((event.key === 'Enter' || event.key === ' ') && event.target.classList.contains('bz-ax')) {
        event.preventDefault();
        event.target.click();
      }
    });
  };

  /* ---------- Faixa de vídeo com parallax ---------- */
  // Cada quadro de rolagem roda em duas fases: primeiro mede tudo (measure*), depois escreve
  // tudo (paint*). Medir depois de escrever força o navegador a refazer o layout no meio do
  // quadro — com três efeitos alternando leitura e escrita, eram três layouts por quadro.
  // Fora da tela (IntersectionObserver), nem mede: a rolagem do resto da página fica de graça.
  const parallax = [];
  const near = (list) =>
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) =>
            entries.forEach((entry) => {
              const item = list.find((it) => it.watch === entry.target);
              if (item) item.near = entry.isIntersecting;
            }),
          { rootMargin: '200px 0px' }
        )
      : null;
  const parallaxNear = near(parallax);
  const measureParallax = () => {
    const vh = window.innerHeight;
    const rad = window.innerWidth < 768 ? 18 : 26;
    parallax.forEach((item) => {
      item.next = null;
      if (!item.near) return;
      const r = item.box.getBoundingClientRect();
      if (r.bottom < -200 || r.top > vh + 200) return;
      const p = (r.top + r.height / 2 - vh / 2) / (vh / 2 + r.height / 2);
      const out = Math.max(0, Math.min(1, -p));
      const inn = Math.max(0, Math.min(1, p));
      const ins = Math.max(out, inn * 0.6);
      // Arredonda para não reescrever estilos por diferenças que não aparecem na tela
      const f = (n) => Math.round(n * 10) / 10;
      item.next = {
        media: `translate3d(0,${f(p * 14)}%,0) scale(1.05)`,
        clip: `inset(${f(out * 38)}% ${f(ins * 6)}% ${f(inn * 20)}% ${f(ins * 6)}% round ${rad}px)`,
        opacity: String(f(1 - Math.min(1, out * 1.6))),
        text: `translate3d(0,${Math.round(-out * 30)}px,0)`,
      };
    });
  };
  const paintParallax = () => {
    parallax.forEach((item) => {
      const next = item.next;
      if (!next) return;
      const last = item.last || {};
      if (item.media && next.media !== last.media) item.media.style.transform = next.media;
      if (next.clip !== last.clip) item.box.style.clipPath = next.clip;
      if (item.text && next.opacity !== last.opacity) item.text.style.opacity = next.opacity;
      if (item.text && next.text !== last.text) item.text.style.transform = next.text;
      item.last = next;
    });
  };
  const runParallax = () => {
    measureParallax();
    paintParallax();
  };
  const initParallax = (box) => {
    if (box.dataset.bzReady || reduce) return;
    box.dataset.bzReady = 'true';
    // near: true até o observador responder, para a primeira posição sair já (a faixa pode nascer na tela)
    parallax.push({ box, watch: box, near: true, media: $('.bz-vb-media', box), text: $('.in', box) });
    if (parallaxNear) parallaxNear.observe(box);
    runParallax();
  };

  /* ---------- Rodapé ---------- */
  const footers = [];
  const footersNear = near(footers);
  const measureFooters = () => {
    const vh = window.innerHeight;
    footers.forEach((item) => {
      item.next = null;
      if (!item.near) {
        // Longe da tela: estado de repouso (p = 0), escrito uma única vez
        if (item.lastP !== 0) item.next = { p: 0, word: false };
        return;
      }
      const r = item.footer.getBoundingClientRect();
      const p = Math.round(Math.max(0, Math.min(1, (vh - r.top) / (vh * 0.85))) * 1000) / 1000;
      const word = !!item.word && !item.footer.classList.contains('in') && item.word.getBoundingClientRect().top < vh * 0.95;
      if (p !== item.lastP || word) item.next = { p, word };
    });
  };
  const paintFooters = () => {
    footers.forEach((item) => {
      const next = item.next;
      if (!next) return;
      if (next.p !== item.lastP && item.inner) {
        item.inner.style.transform = `translate3d(0,${Math.round(-(1 - next.p) * 140)}px,0)`;
        item.inner.style.opacity = (0.25 + 0.75 * next.p).toFixed(3);
      }
      item.lastP = next.p;
      if (next.word) item.footer.classList.add('in');
    });
  };
  const runFooters = () => {
    measureFooters();
    paintFooters();
  };
  const initFooter = (footer) => {
    if (footer.dataset.bzReady) return;
    footer.dataset.bzReady = 'true';
    const cols = $('[data-bz-ft-cols]', footer);
    if (cols) {
      const mobile = window.matchMedia('(max-width: 767px)');
      const sync = () => $$('details.footer__block', cols).forEach((details) => (details.open = !mobile.matches));
      mobile.addEventListener('change', sync);
      sync();
    }
    const form = $('.bz-ft-nl-f', footer);
    if (form) {
      form.setAttribute('novalidate', '');
      form.addEventListener('submit', (event) => {
        const email = $('input[type="email"]', form);
        const msg = $('.bz-nl-msg', form);
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value.trim())) {
          event.preventDefault();
          msg.textContent = msg.dataset.err;
          msg.classList.add('err');
          email.focus();
          return;
        }
        event.preventDefault();
        fetch(form.action, { method: 'POST', body: new FormData(form), headers: { Accept: 'text/html' } }).catch(() => {});
        msg.textContent = msg.dataset.ok;
        msg.classList.remove('err');
        form.reset();
      });
    }
    if (reduce || design) {
      footer.classList.add('in');
      return;
    }
    footers.push({ footer, watch: footer, near: true, inner: $('[data-bz-footer-in]', footer), word: $('.bz-ft-word', footer) });
    if (footersNear) footersNear.observe(footer);
    runFooters();
  };

  /* ---------- Barra fixa do quiz (celular) ---------- */
  const stickies = [];
  const measureSticky = () => {
    if (!stickies.length) return;
    const vh = window.innerHeight;
    const mobile = window.innerWidth < 768;
    let heroGone = false;
    let footerAway = true;
    if (mobile) {
      const hero = $('.bz-banners') || $('main .shopify-section');
      const footer = $('[data-bz-footer]');
      heroGone = hero ? hero.getBoundingClientRect().bottom < 0 : window.scrollY > 600;
      footerAway = footer ? footer.getBoundingClientRect().top > vh : true;
    }
    const locked = document.body.classList.contains('bz-lock');
    stickies.forEach((item) => {
      if (!mobile) {
        item.next = false;
        return;
      }
      const q = item.section.getBoundingClientRect();
      item.next = heroGone && !(q.top < vh && q.bottom > 0) && footerAway && !locked;
    });
  };
  const paintSticky = () => {
    stickies.forEach((item) => {
      if (item.next === item.on) return;
      item.on = item.next;
      item.bar.classList.toggle('on', item.on);
      item.bar.setAttribute('aria-hidden', String(!item.on));
    });
  };
  const runSticky = () => {
    measureSticky();
    paintSticky();
  };

  /* ---------- Página de categoria: filtros, ordenação e "carregar mais" sem recarregar ---------- */
  const collectionRoot = () => $('[data-bz-collection]');
  const withSection = (url, id) => {
    const u = new URL(url, window.location.href);
    u.searchParams.set('section_id', id);
    return u.toString();
  };
  const loadCollection = async (url, push = true) => {
    const rootEl = collectionRoot();
    if (!rootEl) return;
    const id = rootEl.dataset.sectionId;
    closePanels();
    rootEl.classList.add('is-loading');
    try {
      const html = await fetch(withSection(url, id)).then((r) => r.text());
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-bz-collection]');
      if (!fresh) throw new Error('sem conteúdo');
      rootEl.replaceWith(fresh);
      const clean = new URL(url, window.location.href);
      clean.searchParams.delete('section_id');
      clean.searchParams.delete('page');
      if (push) window.history.pushState({ bzCollection: true }, '', clean.toString());
      boot(fresh.parentElement || document);
      const bar = $('.bz-col-bar, .bz-cat-toolbar', fresh);
      if (bar && bar.getBoundingClientRect().top < 0) bar.scrollIntoView({ behavior: smooth });
    } catch (error) {
      window.location.href = url;
    }
  };
  const collectionUrlFromForm = (form) => {
    const params = new URLSearchParams();
    new FormData(form).forEach((value, key) => {
      if (String(value).trim() === '') return;
      // Faixa de preço do filtro ("100-150", "150-") vira o filtro nativo de preço da Shopify
      if (key === 'bz_price') {
        const [min, max] = String(value).split('-');
        if (min) params.append('filter.v.price.gte', min);
        if (max) params.append('filter.v.price.lte', max);
        return;
      }
      params.append(key, value);
    });
    const base = form.getAttribute('action') || window.location.pathname;
    const query = params.toString();
    return query ? `${base}?${query}` : base;
  };
  document.addEventListener('submit', (event) => {
    const form = event.target.closest && event.target.closest('[data-bz-col-form]');
    if (!form) return;
    event.preventDefault();
    loadCollection(collectionUrlFromForm(form));
  });
  document.addEventListener('change', (event) => {
    // Faixas de preço: uma por vez (a Shopify aceita um único intervalo)
    if (event.target.matches && event.target.matches('[data-bz-price]') && event.target.checked) {
      $$('[data-bz-price]', event.target.form).forEach((box) => {
        if (box !== event.target) box.checked = false;
      });
    }
    // Filtros da barra lateral: no computador aplicam ao marcar; no celular, em "Ver resultados"
    const lg = event.target.closest && event.target.closest('[data-bz-col-auto-lg]');
    if (lg && lg.form && window.innerWidth >= 1024) {
      if (lg.form.requestSubmit) lg.form.requestSubmit();
      else loadCollection(collectionUrlFromForm(lg.form));
      return;
    }
    const select = event.target.closest && event.target.closest('[data-bz-col-auto]');
    if (select && select.form) {
      if (select.form.requestSubmit) select.form.requestSubmit();
      else loadCollection(collectionUrlFromForm(select.form));
    }
  });
  window.addEventListener('popstate', () => {
    if (collectionRoot()) loadCollection(window.location.href, false);
  });
  const loadMore = async (link) => {
    const rootEl = collectionRoot();
    const grid = $('[data-bz-col-grid]', rootEl);
    const more = link.closest('[data-bz-col-more]');
    if (!grid || !more) return;
    link.setAttribute('aria-busy', 'true');
    link.classList.add('is-loading');
    try {
      const html = await fetch(withSection(link.href, rootEl.dataset.sectionId)).then((r) => r.text());
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const nextGrid = doc.querySelector('[data-bz-col-grid]');
      if (nextGrid) Array.from(nextGrid.children).forEach((card) => grid.append(card));
      revealCards(grid);
      const nextMore = doc.querySelector('[data-bz-col-more]');
      if (nextMore) more.replaceWith(nextMore);
      else more.remove();
    } catch (error) {
      window.location.href = link.href;
    }
  };

  /* ---------- Rolagem: um único quadro por vez para tudo que depende da posição ---------- */
  let ticking = false;
  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      ticking = false;
      measureParallax();
      measureFooters();
      measureSticky();
      paintParallax();
      paintFooters();
      paintSticky();
    });
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);

  /* ---------- Cliques delegados ---------- */
  document.addEventListener('click', async (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const colLink = target.closest('[data-bz-col-link]');
    if (colLink && collectionRoot()) {
      event.preventDefault();
      loadCollection(colLink.href);
      return;
    }
    const moreLink = target.closest('[data-bz-more]');
    if (moreLink) {
      event.preventDefault();
      loadMoreInto(moreLink);
      return;
    }
    const nextPage = target.closest('[data-bz-col-next]');
    if (nextPage && collectionRoot()) {
      event.preventDefault();
      loadMore(nextPage);
      return;
    }

    const opener = target.closest('[data-bz-open]');
    // data-bz-open-below: só abre a gaveta abaixo dessa largura (acima o painel já está na tela)
    if (opener && opener.dataset.bzOpenBelow && window.innerWidth >= Number(opener.dataset.bzOpenBelow)) return;
    if (opener) {
      event.preventDefault();
      openPanel(opener.dataset.bzOpen);
      return;
    }
    if (target.closest('[data-bz-close]') || target.matches('[data-bz-scrim]')) {
      event.preventDefault();
      closePanels();
      return;
    }
    const accordion = target.closest('[data-bz-accordion]');
    if (accordion) {
      const item = accordion.parentElement;
      const open = !item.classList.contains('open');
      $$('.bz-mm-item.open', item.parentElement).forEach((other) => {
        other.classList.remove('open');
        const b = $('[data-bz-accordion]', other);
        if (b) b.setAttribute('aria-expanded', 'false');
      });
      item.classList.toggle('open', open);
      accordion.setAttribute('aria-expanded', String(open));
      return;
    }
    const chip = target.closest('[data-bz-chip]');
    if (chip) {
      const panel = chip.closest('[data-bz-search]');
      const input = $('[data-bz-search-input]', panel);
      input.value = chip.textContent.trim();
      runSearch(panel);
      return;
    }

    // Link de âncora dentro do menu do celular: fecha a gaveta antes de rolar
    const anchor = target.closest('#bz-mm a[href^="#"]');
    if (anchor) {
      event.preventDefault();
      closePanels();
      const dest = document.querySelector(anchor.getAttribute('href'));
      if (dest) requestAnimationFrame(() => dest.scrollIntoView({ behavior: smooth }));
      return;
    }

    // Card de necessidade: filtra a vitrine da página em vez de abrir a coleção
    const need = target.closest('[data-bz-need]');
    if (need) {
      const railSection = $('[data-bz-rail-section]');
      const handle = need.dataset.bzNeed;
      const hasProducts = railSection && $$('.bz-card', railSection).some((card) => ` ${card.dataset.cols || ''} `.includes(` ${handle} `));
      if (railSection && railSection.bzShowNeed && hasProducts) {
        event.preventDefault();
        closePanels();
        railSection.bzShowNeed(handle, need.dataset.bzNeedLabel || '');
        railSection.scrollIntoView({ behavior: smooth });
      }
      return;
    }

    // Produto com variantes: gaveta "Adicionar rápido" (Ctrl/Cmd+clique continua abrindo o link)
    const quickOpener = target.closest('[data-bz-quick]');
    if (quickOpener && !(event.metaKey || event.ctrlKey || event.shiftKey)) {
      event.preventDefault();
      openQuick(quickOpener);
      return;
    }

    // "Comprar" do card: adiciona, mostra "Na sacola" e abre a sacola
    const buy = target.closest('[data-bz-buy]');
    if (buy) {
      event.preventDefault();
      const config = document.querySelector('[data-bz-toast-config]');
      const original = buy.innerHTML;
      buy.disabled = true;
      try {
        await addToCart(buy.dataset.bzBuy);
        buy.classList.add('added');
        buy.innerHTML = `<svg class="bz-i" viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12 4.5 4.5L19 7"/></svg>${(config && config.dataset.inCart) || 'Na sacola'}`;
        if (window.themeCart) window.themeCart.open();
        setTimeout(() => {
          buy.classList.remove('added');
          buy.innerHTML = original;
        }, 1600);
      } catch (error) {
        window.location.href = `${root}cart/add?id=${buy.dataset.bzBuy}&quantity=1`;
      } finally {
        buy.disabled = false;
      }
      return;
    }

    // Botões "Quero o kit" / "Comprar kit": adiciona e mostra o aviso
    const add = target.closest('[data-bz-add]');
    if (add) {
      event.preventDefault();
      try {
        await addToCart(add.dataset.bzAdd);
        toast(toastText(add.dataset.bzAddName));
      } catch (error) {
        window.location.href = `${root}cart/add?id=${add.dataset.bzAdd}&quantity=1`;
      }
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closePanels();
    const add = event.target instanceof Element && event.target.closest('[data-bz-add][role="button"]');
    if (add && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      add.click();
    }
  });

  document.addEventListener('input', (event) => {
    if (event.target.matches && event.target.matches('[data-bz-search-input]')) runSearch(event.target.closest('[data-bz-search]'));
  });

  /* ---------- Início (e reinício quando o editor recarrega uma seção) ---------- */
  /* Grade da categoria: cards entram em cascata (como no protótipo) */
  const revealCards = (grid) => {
    const cards = $$('.bz-card:not(.in)', grid);
    if (reduce) {
      cards.forEach((card) => card.classList.add('in'));
      return;
    }
    requestAnimationFrame(() => {
      cards.forEach((card, i) => {
        card.style.transitionDelay = `${Math.min(i, 7) * 40}ms`;
        requestAnimationFrame(() => card.classList.add('in'));
      });
    });
  };

  /* Artigo do blog: 1º parágrafo, sumário dos h2, caixas de produto no trecho certo, progresso, copiar link */
  const initArticle = (root) => {
    if (root.dataset.bzReady) return;
    root.dataset.bzReady = 'true';
    const text = $('[data-bz-ar-text]', root);
    if (!text) return;
    const first = $(':scope > p', text);
    if (first) first.classList.add('bz-ar-first');
    const heads = $$(':scope > h2', text);
    const slug = (s) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    // Caixas de produto: vão para o fim do trecho do título nº N (antes do título seguinte)
    $$('[data-bz-ar-after]', text).forEach((box) => {
      const n = Number(box.dataset.bzArAfter) || 0;
      if (n > 0 && heads[n]) text.insertBefore(box, heads[n]);
      else if (n > 0 && heads[n - 1]) {
        const tail = $('.bz-ar-note, .bz-ar-tags', text);
        text.insertBefore(box, tail || null);
      }
    });
    const toc = $('[data-bz-ar-toc]', root);
    const links = heads.map((h, i) => {
      if (!h.id) h.id = slug(h.textContent) || `secao-${i + 1}`;
      const a = document.createElement('a');
      a.href = `#${h.id}`;
      a.textContent = (h.lastChild && h.lastChild.nodeType === 3 ? h.lastChild.textContent : h.textContent).trim();
      if (toc) toc.append(a);
      return a;
    });
    if (toc && links.length) toc.hidden = false;
    const bar = $('[data-bz-ar-bar]', root);
    const article = $('.bz-ar', root);
    let tick = false;
    const progress = () => {
      tick = false;
      if (bar && article) {
        const r = article.getBoundingClientRect();
        const p = Math.max(0, Math.min(1, -r.top / Math.max(1, r.height - window.innerHeight)));
        bar.style.transform = `scaleX(${p})`;
      }
      let cur = 0;
      heads.forEach((h, i) => {
        if (h.getBoundingClientRect().top < window.innerHeight * 0.35) cur = i;
      });
      links.forEach((a, i) => a.classList.toggle('on', i === cur));
    };
    window.addEventListener('scroll', () => {
      if (!tick) {
        tick = true;
        requestAnimationFrame(progress);
      }
    }, { passive: true });
    progress();
    const copy = $('[data-bz-ar-copy]', root);
    if (copy) {
      const label = copy.textContent;
      copy.addEventListener('click', async () => {
        try {
          await navigator.clipboard.writeText(window.location.href);
          copy.textContent = copy.dataset.done;
        } catch (error) {
          copy.textContent = copy.dataset.fail;
        }
        setTimeout(() => (copy.textContent = label), 1800);
      });
    }
  };

  /* "Carregar mais" genérico (blog): acrescenta os itens da próxima página na grade */
  const loadMoreInto = async (link) => {
    const root = link.closest('[data-bz-more-root]');
    const grid = root && $('[data-bz-more-grid]', root);
    const wrap = link.closest('[data-bz-more-wrap]');
    if (!grid || !wrap) return;
    link.setAttribute('aria-busy', 'true');
    try {
      const html = await fetch(withSection(link.href, root.dataset.sectionId)).then((r) => r.text());
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const nextGrid = doc.querySelector('[data-bz-more-grid]');
      if (nextGrid) Array.from(nextGrid.children).forEach((item) => grid.append(item));
      const nextWrap = doc.querySelector('[data-bz-more-wrap]');
      if (nextWrap) wrap.replaceWith(nextWrap);
      else wrap.remove();
    } catch (error) {
      window.location.href = link.href;
    }
  };

  const boot = (scope = document) => {
    $$('.bz-pgrid', scope).forEach(revealCards);
    $$('[data-bz-article]', scope).forEach(initArticle);
    $$('[data-bz-marquee]', scope).forEach(initMarquee);
    if (scope.matches && scope.matches('[data-bz-marquee]')) initMarquee(scope);
    initReveal(scope);
    $$('[data-bz-header]', scope).forEach(initHeader);
    $$('[data-bz-hero]', scope).forEach(initHero);
    $$('.bz', scope).forEach((section) => {
      if ($('[data-bz-rail]', section)) initRail(section);
    });
    $$('[data-bz-bento]', scope).forEach(initBento);
    $$('[data-bz-parallax]', scope).forEach(initParallax);
    $$('[data-bz-footer]', scope).forEach(initFooter);
    $$('[data-bz-sticky]', scope).forEach((bar) => {
      if (bar.dataset.bzReady) return;
      bar.dataset.bzReady = 'true';
      stickies.push({ bar, section: bar.closest('section') || bar.parentElement });
    });
    onScroll();
  };

  let resizeTimer = null;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => $$('[data-bz-marquee]').forEach(initMarquee), 200);
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
  else boot();
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => $$('[data-bz-marquee]').forEach(initMarquee));

  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest('.bz-slide');
    const hero = slide && slide.closest('[data-bz-hero]');
    if (hero && hero.bzGo) hero.bzGo($$('.bz-slide', hero).indexOf(slide));
  });

  window.lumina = { toast, openPanel, closePanels };
})();
